import React, { useEffect, useState } from "react";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import CurrencyFormat from "react-currency-format";
import {calculateCost} from "../../logic/Product";
import {IWorkspaceItem} from "../../models/Workspace";
import { Icon, TooltipHost, Text } from "@fluentui/react";
import {IProductBundleItem} from "../../models/ProductBundles";
import {useEstimateProductBundlePriceCostMutation} from "../../store/endpoints/ProductBundleEndpoints";
import {openEntity} from "../GlobalSearch/useGlobalSearchHotkey";
import VendorPeek from "./VendorPeek";

interface ICostDisplayProps {
    /** How a sourced cost is marked: a solid green cell (workspace grid) or green text on a plain cell (search). */
    affirmative?: 'fill' | 'text';
    source?: string;
    product?: INormalizedProduct;
    cost?: number;
    minTotal?: number;
    maxTotal?: number;
    wsItem?: IWorkspaceItem;
    pbItem?: IProductBundleItem;
    workspaceDefaultMarkup?: number;
    workspaceDefaultMarkupType?: string;
    /** When set, clicking the cell opens this product's details on its Vendor tab. */
    openProductId?: string;
}

export const CostDisplay: React.FC<ICostDisplayProps> = (props) => {
    const [getPriceCostEstimate] = useEstimateProductBundlePriceCostMutation();
    const openProductId = props.openProductId;
    // Clicking the cost opens the product's Vendor tab (where the sourcing behind the number lives).
    const openVendors = openProductId
        ? (e: React.MouseEvent) => { e.stopPropagation(); openEntity({kind: 'product', productId: openProductId, initialTab: 'vendor'}); }
        : undefined;
    const clickProps = openVendors ? {onClick: openVendors, style: {cursor: 'pointer'}, title: 'Open the vendor details'} : {};

    // State for bundle cost and loading
    const [bundleCost, setBundleCost] = useState<number | undefined>(undefined);
    const [bundleCostLoading, setBundleCostLoading] = useState(false);
    const [bundleCostError, setBundleCostError] = useState<string | undefined>(undefined);
    const [inStock, setInStock] = useState<boolean>(false);

    useEffect(() => {
        if (props.wsItem?.isBundle && props.wsItem?.sourceEntityId) {
            setBundleCostLoading(true);
            setBundleCost(undefined);
            setBundleCostError(undefined);
            // Cost must reflect the items actually on this bundle, not the bundle's full default:
            // the order's selected items (bundleSelections), else the rep's prepared configuration,
            // else undefined so the server prices the default+required config.
            const selection = (props.wsItem.bundleSelections?.length ? props.wsItem.bundleSelections
                : props.wsItem.preparedConfiguration?.length ? props.wsItem.preparedConfiguration
                : undefined);
            // An always-included line still gets a cost: the server prices unsourceable items at their lowest known vendor cost.
            const alwaysAvailable = props.wsItem.details?.alwaysAvailable === true;
            getPriceCostEstimate({ bundleId: props.wsItem.sourceEntityId, request: {markup: props.workspaceDefaultMarkup, markupType: props.workspaceDefaultMarkupType, selection, alwaysAvailable} })
                .unwrap()
                .then(res => {
                    setBundleCost(res.cost);
                    setBundleCostLoading(false);
                })
                .catch(() => {
                    setBundleCost(undefined);
                    setBundleCostLoading(false);
                    setBundleCostError("Failed to fetch bundle cost");
                });
        } else {
            setBundleCost(undefined);
            setBundleCostLoading(false);
            setBundleCostError(undefined);
        }
    }, [props.wsItem?.isBundle, props.wsItem?.sourceEntityId, props.workspaceDefaultMarkup, props.workspaceDefaultMarkupType,
        JSON.stringify(props.wsItem?.bundleSelections), JSON.stringify(props.wsItem?.preparedConfiguration), props.wsItem?.details?.alwaysAvailable, getPriceCostEstimate]);


    useEffect(() => {
        setInStock(false)
        // An always-included bundle is priced with unsourceable items at their lowest known cost, so its
        // figure is an override, not an in-stock price: it stays neutral.
        if(bundleCost && !props.wsItem?.details?.alwaysAvailable){
            setInStock(true)
        }
        else if(props.product?.vendors?.some(v => v.inStock || v.onHand && v.onHand > 0)){
            setInStock(true)
        }
    }, [props.product?.vendors, bundleCost, props.wsItem?.details?.alwaysAvailable]);

    function formatCurrency(amount: number) {
        if (amount == null) return null;
        return <CurrencyFormat
            value={amount}
            displayType="text"
            thousandSeparator=","
            prefix="$"
            decimalScale={2}
            fixedDecimalScale={true}
        />;
    }

    const calculateSelectedCost = (): { cost?: number, message?: string, icon?: React.ReactElement } | undefined => {
        if (props.cost) return {
            cost: props.cost
        };
        if (props.wsItem?.details?.overrideCost) return {
            cost: props.wsItem.details.overrideCost,
            message: "Override"
        };
        if (props.wsItem?.details?.discountedCost) return {
            cost: props.wsItem.details.discountedCost,
            icon: <Icon iconName='Coupon' />,
            message: "Discounted (Quantity Price Break)"
        };
        if (props.wsItem?.isBundle && props.wsItem?.sourceEntityId) {
            if (bundleCostLoading) {
                return {
                    cost: undefined,
                    message: "Loading bundle cost..."
                };
            }
            if (bundleCostError) {
                return {
                    cost: undefined,
                    message: bundleCostError
                };
            }
            if (bundleCost !== undefined) {
                return {
                    cost: bundleCost,
                    message: props.wsItem.details?.alwaysAvailable === true
                        ? "Bundle Estimate (always included: unsourceable items at lowest known vendor cost)"
                        : "Bundle Estimate"
                };
            }
            return {
                cost: undefined,
                message: "No bundle cost available"
            };
        }
        if (props.wsItem?.adhoc?.cost) return {
            cost: props.wsItem.adhoc.cost,
            message: "AdHoc"
        };
        if (props.product) {
            const cost = calculateCost(props.product, props.source);
            // An always-included line usually has no in-stock sourcing at all; the lowest known
            // vendor cost is still the honest planning number, so show it rather than N/A.
            if ((cost === undefined || cost === 0) && props.wsItem?.details?.alwaysAvailable === true) {
                const fallback = calculateCost(props.product, 'LowestPrice');
                if (fallback) return {
                    cost: fallback,
                    icon: <Icon iconName='Info' />,
                    message: "Always included: lowest known vendor cost, no vendor has stock."
                };
            }
            return { cost };
        }
        return undefined;
    }

    const renderRange = () => {
        if (props.product) {
            if (props.product.lowestCost && props.product.highestCost) {
                return <>
                    {formatCurrency(props.product.lowestCost)} - {formatCurrency(props.product.highestCost)}
                </>;
            }
        }
        if (props.minTotal && props.maxTotal) {
            return <>
                {formatCurrency(props.minTotal)} - {formatCurrency(props.maxTotal)}
            </>;
        }
        return undefined;
    }

    const selectedCost = calculateSelectedCost();
    const range = renderRange();

    const affirmativeClass = props.affirmative === 'text' ? 'in-stock-text' : 'in-stock-dense';
    const negativeClass = props.affirmative === 'text' ? 'cell-neutral' : 'no-stock-dense';
    // No cost to show is not an affirmative: neutral, never green, whatever the stock says.
    if ((selectedCost?.cost === undefined || selectedCost.cost === 0) && range === undefined) return (
        <VendorPeek product={props.product} highlight={props.source}>
            <span className='cost-unavailable figure-cell' {...clickProps}>
                <span className='figure-main' style={{fontWeight: 400}}>N/A</span>
            </span>
        </VendorPeek>
    );

    return <VendorPeek product={props.product} highlight={props.source}><div className={`${inStock ? affirmativeClass : negativeClass} figure-cell`} {...clickProps}>
        <span className='figure-top' />
        <span className='figure-main'>
            {selectedCost?.cost ? <>
                {/* Fluent Text paints its own theme color; inherit so the green in-stock cell shows white text like the stock cell. */}
                <Text variant='mediumPlus' style={{fontWeight: 'bold', color: 'inherit'}} >
                    {formatCurrency(selectedCost.cost)}
                </Text>
                {selectedCost.icon &&
                    <TooltipHost content={selectedCost.message}>
                        {selectedCost.icon}
                    </TooltipHost>}
            </> : null}
        </span>
        <span className='figure-bottom'>{range}</span>
    </div></VendorPeek>
};